import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '@/config/api.enpoints';
import { CircularProgress, Dialog, IconButton } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';

interface AISummaryBubbleProps {
  pageContext: string;
  pageType: string;
}

export const AISummaryBubble: React.FC<AISummaryBubbleProps> = ({ pageContext, pageType }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [summaryPoints, setSummaryPoints] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  const handleOpen = async () => {
    setIsOpen(true);
    // If we already have the summary, don't fetch again
    if (summaryPoints.length > 0) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/v1/quick-pitch/page-summary`, {
        pageContext,
        pageType
      }, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSummaryPoints(response.data.summary || []);
    } catch (err: any) {
      console.error('Error fetching summary:', err);
      setError('Failed to load summary. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <div 
        className="fixed bottom-24 right-8 z-50 rounded-full shadow-lg cursor-pointer flex items-center justify-center transition-transform hover:scale-110"
        style={{ 
          background: 'linear-gradient(135deg, #6C63FF 0%, #3F3D56 100%)',
          width: '60px',
          height: '60px',
          color: 'white'
        }}
        onClick={handleOpen}
      >
        <AutoAwesomeIcon fontSize="large" />
      </div>

      <Dialog 
        open={isOpen} 
        onClose={handleClose} 
        maxWidth="sm" 
        fullWidth 
        PaperProps={{ 
          className: "bg-white/50 dark:bg-black/40 backdrop-blur-2xl border border-white/60 dark:border-white/10 shadow-[0_8px_32px_0_rgba(31,38,135,0.15)] dark:shadow-[0_8px_32px_0_rgba(0,0,0,0.6)]",
          style: { 
            backgroundImage: 'none',
            borderRadius: '24px',
            margin: '16px' 
          } 
        }}
        slotProps={{
          backdrop: {
            className: "bg-black/10 dark:bg-black/50 backdrop-blur-md"
          }
        }}
      >
        <div className="relative flex flex-col w-full transition-all duration-300">
          {/* Animated gradient accent line at the top */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6C63FF] to-[#3F3D56] opacity-80 rounded-t-3xl" />

          {/* Header */}
          <div className="flex justify-between items-center p-5 border-b border-black/5 dark:border-white/10">
            <div className="flex items-center gap-3">
               <div className="flex items-center justify-center p-2 rounded-full bg-[#6C63FF]/10 text-[#6C63FF]">
                 <AutoAwesomeIcon fontSize="small" />
               </div>
               <span className="text-lg font-semibold tracking-tight text-black/90 dark:text-white">
                 AI Summary: {pageType}
               </span>
            </div>
            <IconButton 
              onClick={handleClose} 
              className="transition-colors text-black/50 hover:text-black hover:bg-black/5 dark:text-white/60 dark:hover:text-white dark:hover:bg-white/10"
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
          
          {/* Content */}
          <div className="p-6">
            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-5">
                <CircularProgress size={32} thickness={5} style={{ color: '#6C63FF' }} />
                <p className="text-sm font-medium animate-pulse text-black/60 dark:text-white/60">
                  Synthesizing intelligence...
                </p>
              </div>
            ) : error ? (
              <div className="p-4 rounded-xl text-center text-sm font-medium bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-400">
                {error}
              </div>
            ) : (
              <div className="flex flex-col gap-5">
                <p className="text-sm font-medium tracking-wide text-black/50 dark:text-white/50">
                  KEY INSIGHTS GENERATED FROM THIS PAGE
                </p>
                <ul className="flex flex-col gap-4">
                  {summaryPoints.map((point, index) => (
                    <li key={index} className="flex gap-4 items-start group">
                      <div className="mt-1.5 p-1 rounded-full shrink-0 transition-all duration-300 group-hover:scale-125 bg-[#6C63FF]/10 dark:bg-[#6C63FF]/20">
                         <div className="w-1.5 h-1.5 rounded-full bg-[#6C63FF]"></div>
                      </div>
                      <span className="text-[15px] leading-relaxed tracking-wide text-black/80 font-normal dark:text-white/90 dark:font-light">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
};
